import React, { useEffect, useMemo, useState } from "react";
import { Search, Sparkles, Zap, ShieldCheck, Clock, MousePointerClick, FileUp, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { TOOLS, CATEGORIES, getToolsByCat } from "../lib/tools";
import { getStats } from "../lib/clientTools";
import ToolCard from "../components/ToolCard";
import AdSlot from "../components/AdSlot";
import Seo from "../components/Seo";

const STEPS = [
  { icon: MousePointerClick, title: "Pick a tool", desc: "Browse 24 free utilities — AI writing, PDF, image, developer and text tools." },
  { icon: FileUp, title: "Paste text or drop a file", desc: "Everything runs instantly in your browser. No account, no waiting." },
  { icon: Download, title: "Grab your result", desc: "Copy or download in one click. Files are processed in-memory and never stored." },
];

export default function Home() {
  const [query, setQuery] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    setStats(getStats());
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return TOOLS.filter(
      (t) => t.name.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q) || t.cat.includes(q)
    );
  }, [query]);

  const featured = TOOLS.filter((t) => t.featured);

  return (
    <div>
      <Seo
        title="ToolHaus — 24 Free Online Tools, No Signup"
        description="Free AI writing, PDF merge and split, image compressor, QR codes and developer utilities. No signup, no watermarks, everything runs in your browser."
        path="/"
      />
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 hero-grid pointer-events-none" aria-hidden />
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="max-w-3xl">
            <div className="chip mb-6" data-testid="hero-pill">
              <Sparkles size={12} className="text-primary" /> 24 free tools · No signup · Nothing stored
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-[1.05]">
              Every everyday tool,
              <br />
              <span className="text-gradient">free in one place.</span>
            </h1>
            <p className="text-lg text-gray-500 mt-6 max-w-2xl leading-relaxed">
              ToolHaus is your free online toolbox: <span className="font-medium text-foreground">summarize and translate with AI</span>,{" "}
              <span className="font-medium text-foreground">merge and compress PDFs</span>,{" "}
              <span className="font-medium text-foreground">resize images</span>, generate QR codes, format JSON and more —
              no signup, no watermarks, and your files are never stored.
            </p>

            <div className="mt-9 relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                data-testid="hero-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search 24 tools — try “summarize”, “pdf”, “qr”…"
                className="w-full h-14 pl-12 pr-4 text-lg rounded-xl border border-border bg-card shadow-sm transition-all focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/15"
              />
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
              {CATEGORIES.map((c) => (
                <Link key={c.id} to={`/category/${c.id}`}
                  className="chip hover:border-primary/40 transition-colors"
                  data-testid={`hero-chip-${c.id}`}>
                  <c.icon size={12} style={{ color: c.accent }} /> {c.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Trust strip */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Zap, label: "Tools run", value: stats ? new Intl.NumberFormat().format(stats.tools_run) : "—" },
              { icon: Clock, label: "Files processed", value: stats ? new Intl.NumberFormat().format(stats.files_processed) : "—" },
              { icon: ShieldCheck, label: "Sign-ups required", value: "Zero" },
              { icon: Sparkles, label: "Tools available", value: stats ? stats.tools_available : 24 },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-3 p-4 rounded-lg border border-border bg-card" data-testid={`stat-${i}`}>
                <s.icon size={18} className="text-primary" />
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] text-gray-400 font-semibold">{s.label}</div>
                  <div className="font-heading text-lg font-semibold">{s.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 space-y-20">
        {filtered !== null && (
          <section>
            <h2 className="font-heading text-2xl font-medium mb-6">
              {filtered.length} result{filtered.length === 1 ? "" : "s"} for “{query}”
            </h2>
            {filtered.length === 0 ? (
              <p className="text-gray-500" data-testid="search-empty">No matches. Try a broader term.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
                {filtered.map((t, i) => <ToolCard key={t.slug} tool={t} index={i} />)}
              </div>
            )}
          </section>
        )}

        {filtered === null && (
          <>
            {/* How it works */}
            <section data-testid="section-how" className="rounded-2xl border border-border bg-card p-8 sm:p-10">
              <div className="flex items-end justify-between mb-8">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-primary">How it works</div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-medium mt-1">Get a small job done in seconds</h2>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {STEPS.map((s, i) => (
                  <div key={i} className="flex gap-4" data-testid={`how-step-${i}`}>
                    <div className="step-badge shrink-0">{i + 1}</div>
                    <div>
                      <h3 className="font-heading font-semibold flex items-center gap-2">
                        <s.icon size={16} className="text-primary" /> {s.title}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Popular bento grid */}
            <section data-testid="section-popular">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-gray-400">Most used</div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-medium mt-1">Popular this week</h2>
                </div>
                <Link to="/category/ai" className="text-sm text-primary hover:underline">Browse AI →</Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
                {featured.map((t, i) => <ToolCard key={t.slug} tool={t} featured index={i} />)}
              </div>
            </section>

            <AdSlot variant="leaderboard" testid="ad-home-mid" />

            {CATEGORIES.map((cat) => {
              const tools = getToolsByCat(cat.id);
              if (!tools.length) return null;
              return (
                <section key={cat.id} data-testid={`section-${cat.id}`}>
                  <div className="flex items-end justify-between mb-6">
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: cat.accent }}>
                        {cat.label}
                      </div>
                      <h2 className="font-heading text-2xl sm:text-3xl font-medium mt-1">
                        {cat.id === "ai" && "Powerful AI, plain English"}
                        {cat.id === "pdf" && "Wrangle any PDF"}
                        {cat.id === "image" && "Image work, simplified"}
                        {cat.id === "dev" && "Dev tools for daily flow"}
                        {cat.id === "text" && "Text utilities that just work"}
                      </h2>
                    </div>
                    <Link to={`/category/${cat.id}`} className="text-sm text-primary hover:underline" data-testid={`see-all-${cat.id}`}>
                      See all →
                    </Link>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
                    {tools.map((t, i) => <ToolCard key={t.slug} tool={t} index={i} />)}
                  </div>
                </section>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
